import React, { useEffect, useLayoutEffect, useState } from 'react';
//CSS
import styles from './Popup.module.css';
import ReactDOM from 'react-dom';
import {
  readPopupDefaultPinToSide,
  writePopupDefaultPinToSide,
} from '../../../Helpers/popupPinPreference';
//Components
//Data

type PopupProps = {
    isVisible: boolean;
    onClose: () => void;
    children?: React.ReactNode;
    style?: React.CSSProperties | undefined;
    allowSnapToSide?: boolean;
    onSnapStateChange?: (isSnappedToSide: boolean) => void;
    /** Document lightbox: larger shell, slim header, full-bleed content. */
    variant?: 'default' | 'document';
    /** Shown in the header (document variant). */
    title?: string;
    /** Raise the overlay when this popup sits on top of another. */
    zIndex?: number;
};

const Popup = ({
  isVisible,
  onClose,
  children,
  style,
  allowSnapToSide = false,
  onSnapStateChange,
  variant = 'default',
  title,
  zIndex,
}: PopupProps) => {
    const [isSnappedToSide, setIsSnappedToSide] = useState(false);
    const [defaultPinPref, setDefaultPinPref] = useState(() => readPopupDefaultPinToSide());
    const isDocument = variant === 'document';

    useLayoutEffect(() => {
      if (!allowSnapToSide) return;
      if (!isVisible) {
        setIsSnappedToSide(false);
        return;
      }
      setDefaultPinPref(readPopupDefaultPinToSide());
      if (readPopupDefaultPinToSide()) {
        setIsSnappedToSide(true);
      }
    }, [isVisible, allowSnapToSide]);

    useEffect(() => {
      if (!allowSnapToSide) return;

      const body = document.body;
      const updateDockedWidth = () => {
        const width = Math.max(320, Math.min(560, window.innerWidth - 32));
        body.style.setProperty('--docked-popup-width', `${width}px`);
      };

      if (isVisible && isSnappedToSide) {
        updateDockedWidth();
        body.classList.add('popup-docked-right');
        window.addEventListener('resize', updateDockedWidth);
      } else {
        body.classList.remove('popup-docked-right');
        body.style.removeProperty('--docked-popup-width');
      }

      return () => {
        window.removeEventListener('resize', updateDockedWidth);
        body.classList.remove('popup-docked-right');
        body.style.removeProperty('--docked-popup-width');
      };
    }, [allowSnapToSide, isVisible, isSnappedToSide]);

    useEffect(() => {
      onSnapStateChange?.(isSnappedToSide);
    }, [isSnappedToSide, onSnapStateChange]);
    
    if (!isVisible) return null;

    const containerClassName = `${styles.container} ${isSnappedToSide ? styles.containerNoModal : ''}`;
    const popupClassName = [
      styles.popup,
      isSnappedToSide ? styles.popupSnappedToSide : '',
      isDocument ? styles.popupDocument : '',
    ]
      .filter(Boolean)
      .join(' ');
    const headerClassName = [
      styles.popup__header,
      isDocument ? styles.popup__headerDocument : '',
    ]
      .filter(Boolean)
      .join(' ');
    const innerClassName = [
      styles.popupInner,
      allowSnapToSide ? styles.popupInnerWithSnap : '',
      isDocument ? styles.popupInnerDocument : '',
    ]
      .filter(Boolean)
      .join(' ');

    const documentStyle: React.CSSProperties | undefined = isDocument
      ? {
          width: 'min(1120px, 96vw)',
          height: '92vh',
          maxHeight: '92vh',
          ...style,
        }
      : style;

    return ReactDOM.createPortal(
        <div
          className={containerClassName}
          style={zIndex ? { zIndex } : undefined}
          onClick={isSnappedToSide ? undefined : onClose}
        >
          <div
            style={documentStyle}
            className={popupClassName}
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the box
          >
            <div className={headerClassName}>
              {allowSnapToSide && (
                <div className={styles.popup__headerLeft}>
                  <button
                    type="button"
                    className={styles.snapButton}
                    onClick={() => setIsSnappedToSide(!isSnappedToSide)}
                    aria-label={isSnappedToSide ? 'Unpin panel' : 'Pin panel to side'}
                  >
                    <img
                      src="/img/pin-angle.svg"
                      alt=""
                      aria-hidden="true"
                      className={`${styles.snapButtonIcon} ${isSnappedToSide ? styles.snapButtonIconActive : ''}`}
                    />
                    <span>{isSnappedToSide ? 'Unpin' : 'Pin to side'}</span>
                  </button>
                  <label className={styles.defaultPinLabel}>
                    <input
                      type="checkbox"
                      className={styles.defaultPinCheckbox}
                      checked={defaultPinPref}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        writePopupDefaultPinToSide(checked);
                        setDefaultPinPref(checked);
                        if (checked) {
                          setIsSnappedToSide(true);
                        }
                      }}
                    />
                    <span>Always pin to side</span>
                  </label>
                </div>
              )}
              {isDocument && title ? (
                <h2 className={styles.popup__title}>{title}</h2>
              ) : (
                !allowSnapToSide && <span className={styles.popup__headerSpacer} />
              )}
              <img src='/img/close.svg' alt="Close popup" onClick={onClose} />
            </div>
            <div className={innerClassName}>
              {children}
            </div>
          </div>
        </div>,
        document.getElementById("popup")!
      );
};

export default Popup;
